import { CircleInfoIcon } from "@xola/icons";
import { flash, FormGroup, getSymbol, ImageUpload, Label, Select, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, RadioInput, SelectField, TextareaField } from "common";
import { useFormikContext } from "formik";
import { capitalize, forEach, isNil } from "lodash";
import React, { ChangeEvent, useEffect } from "react";
import {
    AddOn,
    AddOnType,
    AmountType,
    CatalogItemDisplaySection,
    CatalogItemVisibility,
    CatalogType,
    Field,
    ProductObject,
} from "types";
import { ProductSelectorModal } from "../../../Common/ProductSelectorModal";
import { SelectedProductListInput } from "../../../Common/SelectedProductListInput";
import { CurrencyInputField } from "../../../Field/CurrencyInputField";
import { QuestionFormRosterSettings } from "../../Questionnaire/QuestionFormModal";
import { canMatchGuestCount, getTypeSwitchChanges } from "./AddOnFormUtils";
import { AddOnPurchaseQuantityFields } from "./AddOnPurchaseQuantityFields";
import { AddOnRestrictionsFields } from "./AddOnRestrictionsFields";
import { AddOnEditorFormState } from "./AddOnTypes";
import { DropdownItems } from "./DropdownItems";
import { useQuestionnairePreference } from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useVisibility } from "@/hooks/useVisibility";

const updateDisplaySectionsForOrganizer = (currentDisplaySections: CatalogItemDisplaySection[] = []) => {
    return currentDisplaySections.filter((section) => section !== CatalogItemDisplaySection.QUESTIONNAIRE);
};

const updateDisplaySectionsForAttendee = (currentDisplaySections: CatalogItemDisplaySection[] = []) => {
    const newDisplaySections = currentDisplaySections.filter((section) => section !== CatalogItemDisplaySection.ADDONS);

    if (!newDisplaySections.includes(CatalogItemDisplaySection.QUESTIONNAIRE)) {
        newDisplaySections.push(CatalogItemDisplaySection.QUESTIONNAIRE);
    }

    return newDisplaySections;
};

interface EditAddOnFieldsProps {
    showTargetAudience?: boolean;
    addOn?: AddOn;
    imageUrl: string;
    onImageChange: (file: File) => void;
    onImageDelete: () => void;
    isProductAddon?: boolean;
    field?: Field;
    isQuestionnaire?: boolean;
}

export const EditAddOnFields = ({
    showTargetAudience,
    addOn,
    imageUrl,
    onImageChange,
    onImageDelete,
    isProductAddon,
    field,
    isQuestionnaire,
}: EditAddOnFieldsProps) => {
    const { values, setFieldValue, errors } = useFormikContext<AddOnEditorFormState>();
    const seller = useSeller();
    const sellerCurrencySymbol = getSymbol(seller.currency);
    const [visible, { toggle }] = useVisibility();
    const { viewConfigurations } = useQuestionnairePreference();
    const showRosterSettings = values.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);

    useEffect(() => {
        if (values.type === CatalogType.CHOICES) {
            if (values.items.length === 0) {
                setFieldValue("items", [{ name: "", price: null, priceType: AmountType.ABSOLUTE, id: "1" }]);
            }

            return;
        }

        if (values.items.length > 0) {
            setFieldValue("items", []);
        }
    }, [values.type, values.items, setFieldValue]);

    const isGuestCountMatchable = canMatchGuestCount(values);
    useEffect(() => {
        if (!isGuestCountMatchable && !isNil(values.quantitySource)) {
            setFieldValue("quantitySource", null);
        }
    }, [isGuestCountMatchable, values.quantitySource, setFieldValue]);

    const canShowProductList = !isProductAddon;
    const canEdit = !isProductAddon || !addOn;

    const handleTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
        const type = e.target.value as CatalogType;
        setFieldValue("type", type);
        forEach(getTypeSwitchChanges(type), (value, field) => setFieldValue(field, value));
    };

    return (
        <div className="space-y-4">
            <FormField name="name" label="Name">
                <InputField isRequired disabled={!canEdit} />
            </FormField>
            {showTargetAudience && (
                <FormGroup data-testid="addon-target-audience-section">
                    <Label className="mb-0">Target Audience</Label>
                    <div className="space-y-2">
                        <div
                            className={clsx("mt-2 flex items-center space-x-2", {
                                "opacity-80": isQuestionnaire,
                            })}
                        >
                            <RadioInput
                                checked={!values.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE)}
                                name="targetAudience"
                                disabled={isQuestionnaire}
                                onChange={() => {
                                    const newDisplaySections = updateDisplaySectionsForOrganizer(
                                        values.displaySections,
                                    );
                                    setFieldValue("displaySections", newDisplaySections);
                                }}
                            >
                                Ask organizer only
                            </RadioInput>
                            <Tooltip
                                placement="right"
                                content="These add-ons are presented in the add-ons section in the checkout workflow"
                            >
                                <CircleInfoIcon size="small" className="text-gray-600" />
                            </Tooltip>
                        </div>
                        <div className="flex items-center space-x-2">
                            <RadioInput
                                checked={values.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE)}
                                name="targetAudience"
                                onChange={() => {
                                    const newDisplaySections = updateDisplaySectionsForAttendee(values.displaySections);
                                    setFieldValue("displaySections", newDisplaySections);
                                }}
                            >
                                Ask each attendee
                            </RadioInput>
                            <Tooltip
                                placement="right"
                                content="These add-ons are presented in the questionnaire for each attendee"
                            >
                                <CircleInfoIcon size="small" className="text-gray-600" />
                            </Tooltip>
                        </div>
                    </div>
                </FormGroup>
            )}

            <FormField name="caption" label="Description">
                <TextareaField rows={5} value={values.caption} />
            </FormField>

            {canShowProductList && <SelectedProductListInput selectedProducts={values.products} onEditClick={toggle} />}

            {canShowProductList && (
                <ProductSelectorModal
                    enableSelectAll={false}
                    title="Experience Restrictions"
                    description="Select the experience where this Add-On applies."
                    objects={[ProductObject.EXPERIENCE]}
                    isOpen={visible}
                    value={values.products}
                    onClose={toggle}
                    onConfirm={(selectionState) => {
                        setFieldValue("products", selectionState);
                    }}
                />
            )}

            <FormGroup>
                <Label>Image</Label>
                <fieldset>
                    <ImageUpload
                        src={imageUrl}
                        hasDelete={false}
                        isLoading={false}
                        onChange={onImageChange}
                        onDelete={onImageDelete}
                        onError={(error: string) => flash.danger(error)}
                    />
                </fieldset>
            </FormGroup>

            <FormField name="type" label="Selection Type">
                <Select
                    data-testid="edit-global-addon-fields-selection-Type-dropdown"
                    className="h-full min-w-10 rounded"
                    disabled={addOn?.id}
                    value={values.type}
                    onChange={handleTypeChange}
                >
                    <option value={CatalogType.QUANTITY}>{capitalize(AddOnType.QUANTITY)}</option>
                    <option value={CatalogType.BOOLEAN}>{capitalize(AddOnType.CHECKBOX)}</option>
                    <option value={CatalogType.CHOICES}>{capitalize(AddOnType.DROPDOWN)}</option>
                </Select>
            </FormField>

            {values.type === CatalogType.CHOICES ? (
                <DropdownItems disabled={!canEdit} addOn={addOn} />
            ) : (
                <div className="flex w-full space-x-2">
                    <FormField name="price" label="Price" className="grow">
                        {values.type === CatalogType.BOOLEAN ? (
                            <CurrencyInputField
                                isRequired
                                min="0"
                                {...(values.priceType === AmountType.PERCENT ? { prefix: "%" } : {})}
                                className={clsx(errors.price && "border-danger", "w-52")}
                            />
                        ) : (
                            <CurrencyInputField isRequired min="0" className={clsx(errors.price && "border-danger")} />
                        )}
                    </FormField>
                    {values.type === CatalogType.BOOLEAN ? (
                        <SelectField name="priceType" className="mt-1.5 h-10">
                            <option className="no-translate" value={AmountType.ABSOLUTE}>
                                {sellerCurrencySymbol}
                            </option>
                            <option value={AmountType.PERCENT}>% of purchasable items</option>
                        </SelectField>
                    ) : null}
                </div>
            )}
            <AddOnRestrictionsFields />

            <AddOnPurchaseQuantityFields />

            <FormGroup>
                <Label className="mb-4">Settings</Label>
                <div className="flex flex-col space-y-2">
                    <Switch.Group>
                        <Switch
                            isChecked={values.visibility === CatalogItemVisibility.PRIVATE}
                            onChange={(value: boolean) =>
                                setFieldValue(
                                    "visibility",
                                    value ? CatalogItemVisibility.PRIVATE : CatalogItemVisibility.PUBLIC,
                                )
                            }
                        />

                        <Switch.Label direction="right">
                            Add-on is only available for back office purchases
                        </Switch.Label>
                    </Switch.Group>

                    {(values.type === CatalogType.QUANTITY || values.type === CatalogType.CHOICES) && (
                        <Switch.Group>
                            <Switch
                                isChecked={values.required}
                                disabled={values.visibility === CatalogItemVisibility.PRIVATE}
                                onChange={(value: boolean) => setFieldValue("required", value)}
                            />

                            <Switch.Label
                                direction="right"
                                className={clsx(
                                    values.visibility === CatalogItemVisibility.PRIVATE && "!cursor-not-allowed",
                                )}
                            >
                                Add-on is required
                            </Switch.Label>
                        </Switch.Group>
                    )}
                </div>
            </FormGroup>

            {showTargetAudience && showRosterSettings && (
                <QuestionFormRosterSettings viewConfigurations={viewConfigurations} field={field} />
            )}
        </div>
    );
};
